// Copyright (c) StrangeBee 2024

import React, { ReactElement, useContext, useMemo, useState } from 'react';
import { ICONS } from '@Constants/iconsApp';
import { handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import { OrgCreate } from '@Organisms/Organisation/OrgCreate/OrgCreate';
import { ObjectTypeV1 as ObjectType, AdminPermissionsEnum, PermissionsEnum, OrganisationOutput } from 'thehive-sdk';
import useConfigList from '@Hooks/useConfigList';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import client from '@Client';
import OrganisationListName from '@Organisms/Organisation/OrgListName/OrgListName';
import ListProvider from '@Context/ListProvider';
import RouteBuilder from '@Services/RouteBuilder';
import { Link } from 'react-router-dom';
import { RouteType } from '@Types';
import FormatDate from '@Molecules/Format/FormatDate/FormatDate';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { useCurrentUser } from '@Queries/userQueries';
import { computeEventList } from '@Organisms/Organisation/eventList';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { THList } from '@Molecules/THList';
import { useListAllFields } from '@Queries/describeQueries';
import usePermissions from '@Hooks/usePermissions';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { computeListSelector } from '@Organisms/Organisation/selector';
import THTag from '@Atoms/THTag/THTag';
import { HTMLColors } from '@Constants/colors';
import { findDefaultPath } from '@Helpers/RedirectPath';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import Space from 'antd/lib/space';
import ActionList from '@Molecules/ActionList/ActionList';
import { SelectedInfo } from '@Atoms/SelectedInfo/SelectedInfo';
import { StatusContext } from '@Context/StatusProvider';
import { useDrawer } from '@Hooks/useDrawer';
import { OrganisationDrawer } from '@Organisms/Organisation/OrganisationDrawer/OrganisationDrawer';
import { ExportButton } from '@Organisms/Export/ExportDrawer/ExportButton';
import usePageTitle from '@Hooks/usePageTitle';

import './OrganisationsList.scss';

const OrganisationsList = (): ReactElement => {
    const i = useLocalMessage();

    const { quotas } = useContext(StatusContext);

    const { visible, setVisible, data, setData } = useDrawer<OrganisationOutput>({});
    const { visible: visibleCreate, setVisible: setVisibleCreate } = useDrawer<OrganisationOutput>({});

    const config = useConfigList<OrganisationOutput>({
        selector: computeListSelector(),
        controller: client.OrganisationController,
        storageId: StorageFilterEnum.ORGANISATION,
        objectType: ObjectType.ORGANISATION,
    });

    const {
        dataSource = [],
        actionListRenderer,
        rowSelectionCustomRenderer,
        columnsRenderer,
        listRefetch,
        countRefetch,
        table,
        filterManager,
        status = 'loading',
        paginationManager,
        stateSelectedKeys: { selectedKeys },
        storagePageManager,
        storageViewManager,
        queryParams,
    } = config;

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.OrganisationController,
                locale: i,
                onSuccess: (count, message) => {
                    listRefetch();

                    if (count) {
                        countRefetch();
                    }
                    if (message) {
                        openMessage('success', message);
                    }
                },
                onError: handleNotificationError,
            }),
        [i, listRefetch, countRefetch],
    );

    const { data: currentUser } = useCurrentUser();
    const { permissions = [] } = currentUser || {};

    const [exportDrawerVisible, setExportDrawerVisible] = useState(false);

    const actionList = actionListRenderer((selectedItems, onSuccess) => {
        const orgIdList = selectedItems.map(({ _id }) => _id);
        const isMultiple = orgIdList.length > 1 ? 'multiple' : 'single';

        return {
            lock: {
                query: (): Promise<void> =>
                    client.OrganisationController.multipleEdit({ locked: true }, orgIdList)
                        .then(() => {
                            openMessage('success', i('notification.organisation.edit'));
                            onSuccess();
                        })
                        .catch(handleNotificationError),
                message: i(`organisation.actions.lock.${isMultiple}`),
                label: i('organisation.actions.lock.label'),
                icon: <ICONS.LockIcon />,
                hidden: selectedItems.some(({ locked }: { locked: boolean }) => locked),
                confirm: true,
            },
            unlock: {
                query: (): Promise<void> =>
                    client.OrganisationController.multipleEdit({ locked: false }, orgIdList)
                        .then(() => {
                            openMessage('success', i('notification.organisation.edit'));
                            onSuccess();
                        })
                        .catch(handleNotificationError),
                message: i(`organisation.actions.unlock.${isMultiple}`),
                label: i('organisation.actions.unlock.label'),
                icon: <ICONS.UnlockIcon />,
                hidden: selectedItems.some(({ locked }: { locked: boolean }) => !locked),
            },
        };
    });

    useDrawer.watch(dataSource, () => {
        if (data) {
            setData(dataSource.find(({ _id }) => _id === data._id));
        }
    });

    const hasPermissions = usePermissions({
        permissionsNeeded: [AdminPermissionsEnum.MANAGE_ORGANISATION],
        permissionsList: permissions,
    });

    const defaultPath = findDefaultPath(permissions, {
        [PermissionsEnum.MANAGE_USER]: 'administration.users',
        [PermissionsEnum.MANAGE_CASE_TEMPLATE]: 'administration.caseTemplates',
        [PermissionsEnum.MANAGE_ORGANISATION]: 'administration.links',
        [PermissionsEnum.MANAGE_CONFIG]: 'administration.settings',
    });

    const columns = columnsRenderer([
        {
            className: 'organisations-list__locked',
            render: (data, { bool: filterBool }) => (
                <THTag
                    full
                    value={i(`common.${data.locked ? 'locked' : 'active'}`)}
                    color={data.locked ? HTMLColors.RED : HTMLColors.GREEN}
                    onClick={() => filterBool('locked', data.locked + '')}
                />
            ),
        },
        {
            title: [{ label: i('organisation.list.table.columns.name'), sortField: 'name' }],
            className: 'organisations-list__name column_preview',
            render: (data): ReactElement => (
                <OrganisationListName
                    data={data}
                    path={defaultPath}
                    onPreviewClick={() => {
                        setData(data);
                        setVisible(true);
                    }}
                />
            ),
        },
        {
            title: [{ label: i('organisation.list.table.columns.created-by'), sortField: '_createdBy' }],
            className: 'organisations-list__assignee',
            render: ({ _createdBy }): ReactElement => <AssigneeInfo assignee={_createdBy} />,
        },
        {
            title: [{ label: i('organisation.list.table.columns.created-at'), sortField: '_createdAt' }],
            className: 'organisations-list__date',
            render: ({ _createdAt }): ReactElement => <FormatDate date={_createdAt} />,
        },
    ]);

    const { data: describe } = useListAllFields();

    const dataSourceFiltered = useMemo(() => {
        const adminOrg = dataSource.find(({ name }) => name === 'admin');

        return adminOrg ? [adminOrg, ...dataSource.filter(({ name }) => name !== 'admin')] : dataSource;
    }, [dataSource]);

    const rowSelection = rowSelectionCustomRenderer({
        label: i('common.selected'),
        render: ({ locked }, checkbox): ReactElement => (
            <Space direction="vertical" size={1}>
                {checkbox}
                {locked && <ICONS.LockFilled className="user-list-checkbox-cell__lock" />}
            </Space>
        ),
    });

    usePageTitle(i('page-title.admin.organisation-list'));

    return (
        <>
            <THList>
                <THList.Header>
                    <THList.Slot name="MANAGE">
                        {hasPermissions && (
                            <AddButton
                                label={i('organisation.list.header.add-organisation')}
                                disabled={!quotas.organisations?.valid}
                                onClick={() => setVisibleCreate(true)}
                            />
                        )}
                        {storagePageManager && storageViewManager ? (
                            <ManageViewList
                                pageManager={storagePageManager}
                                viewManager={storageViewManager}
                                beforeCreate={(content: any): any => {
                                    filterManager.applyFilters();
                                    return { ...content, filters: filterManager.tempFilters };
                                }}
                            />
                        ) : null}

                        <ExportButton
                            onClick={() => {
                                setExportDrawerVisible(true);
                            }}
                            objectType="Organisation"
                            selectedItems={dataSource.reduce((acc: string[], data) => {
                                if (selectedKeys.includes(data['_id'])) {
                                    acc.push(data['_id']);
                                }
                                return acc;
                            }, [])}
                            queryParams={queryParams}
                            visible={exportDrawerVisible}
                            afterVisible={setExportDrawerVisible}
                            listNumber={table.count}
                        />

                        <SelectedInfo label={i('common.selected')} count={selectedKeys.length} />

                        {
                            <ActionList
                                actions={actionList(
                                    dataSource.filter((data: { _id: string }) => selectedKeys.includes(data['_id'])),
                                    (count) => {
                                        listRefetch?.();
                                        table?.setSelectedKeys?.([]);
                                        if (count) {
                                            countRefetch?.();
                                        }
                                    },
                                )}
                                scope="list"
                                hidden={!selectedKeys?.length}
                            />
                        }
                    </THList.Slot>
                    <THList.Slot name="NAVIGATION">
                        <ManageSwitch
                            label={i('common.filters')}
                            value={filterManager.editFilters}
                            onChange={(editFilters): void => {
                                filterManager.setEditFilters(editFilters);
                            }}
                            icon={<ICONS.FilterOutlined />}
                        />
                    </THList.Slot>
                    <THList.Slot name="CONTENT">
                        {(filterManager.editFilters || !!filterManager.tempFilters.length) && (
                            <THFilter filterConfig={filterManager} describeField={describe?.[ObjectType.ORGANISATION]}>
                                <Footer filterConfig={filterManager} />
                            </THFilter>
                        )}
                    </THList.Slot>
                </THList.Header>

                <THList.Content
                    dataSource={dataSourceFiltered}
                    rowSelection={hasPermissions ? rowSelection : undefined}
                    columns={columns}
                    rowKey="_id"
                    emptyPlaceholder={
                        <DisplayEmptyData spin={status === 'loading'} label={i('organisation.none')}>
                            {hasPermissions && (
                                <THButton type="link" onClick={() => setVisibleCreate(true)}>
                                    {i('organisation.list.header.add-organisation')}
                                </THButton>
                            )}
                        </DisplayEmptyData>
                    }
                />

                <THList.Footer>
                    <THList.Slot name="NAVIGATION">
                        <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                        <PaginationSize paginationConfig={paginationManager} />
                    </THList.Slot>
                </THList.Footer>
            </THList>

            {data && visible && (
                <OrganisationDrawer visible={visible} onVisibleChange={setVisible} data={data} eventList={eventList} />
            )}
            {visibleCreate && (
                <OrgCreate visible={visibleCreate} onVisibleChange={setVisibleCreate} onCreate={eventList.onCreate} />
            )}
        </>
    );
};

const pathAdmin = RouteBuilder.compute('administration.organizations');
//const pathUser = RouteBuilder.compute('organisation.organizations');

const MenuLink = ({ path }: { path: string }): ReactElement => {
    const local = useLocalMessage();
    return <Link to={path}>{local('mainLayout.adminMenu.organisations')}</Link>;
};

export const Route: RouteType = {
    path: pathAdmin,
    menu: {
        key: 'organisation',
        icon: <ICONS.OrganisationIcon />,
        render: <MenuLink path={pathAdmin} />,
        selectTab: pathAdmin,
        group: 'config',
        space: ['admin'],
    },
    permissionNeeded: [
        PermissionsEnum.MANAGE_ORGANISATION,
        [PermissionsEnum.MANAGE_ORGANISATION, PermissionsEnum.MANAGE_USER],
        [PermissionsEnum.MANAGE_ORGANISATION, PermissionsEnum.MANAGE_CONFIG],
    ],
    breadcrumb: {
        label: { user: 'breadcrumb.global.organisation', admin: 'breadcrumb.global.organisation-list' },
        forcedLink: { user: RouteBuilder.compute('cases.default') },
    },
    content: <OrganisationsList />,
    Context: ListProvider,
    space: ['admin'],
    exact: true,
};
